import { type RGBA } from '../lib/color';
import { toast } from './toast';
import { t } from '../lib/i18n';

export interface PickerRef {
  root: HTMLElement;
  update: () => void;
}

// 声明非标准但已被 Chrome / Edge 支持的 API
interface EyeDropperCtor {
  new (): { open: () => Promise<{ sRGBHex: string }> };
}
declare global {
  interface Window {
    EyeDropper?: EyeDropperCtor;
  }
}

export function createPicker(
  onPick: (c: RGBA) => void,
  onPickImage: (c: RGBA) => void,
  onRandom: () => void
): PickerRef {
  const root = document.createElement('section');
  root.className = 'picker';

  const eyeDropBtn = document.createElement('button');
  eyeDropBtn.type = 'button';
  eyeDropBtn.className = 'btn btn--primary';
  eyeDropBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 22l1-1h3l9-9"/><path d="M3 21v-3l9-9"/><path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z"/></svg> ${t('🎯 Eyedropper').split(' ')[1] || 'Eyedropper'}`;

  const imageBtn = document.createElement('button');
  imageBtn.type = 'button';
  imageBtn.className = 'btn';
  imageBtn.textContent = t('🖼 From Image');

  if (!window.EyeDropper) {
    eyeDropBtn.disabled = true;
    eyeDropBtn.title = t('EyeDropper API not supported in this browser');
    imageBtn.classList.add('btn--highlight');
  } else {
    eyeDropBtn.addEventListener('click', async () => {
      try {
        // @ts-expect-error 运行时类型
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open();
        const hex = result.sRGBHex;
        onPick(hexFromString(hex));
      } catch {
        // 用户取消，忽略
      }
    });
  }
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*';
  fileInput.style.display = 'none';
  imageBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      openImagePicker(img, url, onPickImage);
      fileInput.value = '';
    };
    img.onerror = () => {
      toast(t('Failed to load image'));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });

  const randomBtn = document.createElement('button');
  randomBtn.type = 'button';
  randomBtn.className = 'btn';
  randomBtn.textContent = t('🎲 Random');
  randomBtn.addEventListener('click', onRandom);

  const favBtn = document.createElement('button');
  favBtn.type = 'button';
  favBtn.className = 'btn';
  favBtn.textContent = t('⭐ Favorite');
  favBtn.title = t('Add current color to favorites');

  root.append(eyeDropBtn, imageBtn, randomBtn, favBtn, fileInput);
  (root as HTMLElement & { _favBtn?: HTMLButtonElement })._favBtn = favBtn;

  function update(): void {
    eyeDropBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 22l1-1h3l9-9"/><path d="M3 21v-3l9-9"/><path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z"/></svg> ${t('🎯 Eyedropper').split(' ')[1] || 'Eyedropper'}`;
    imageBtn.textContent = t('🖼 From Image');
    randomBtn.textContent = t('🎲 Random');
    favBtn.textContent = t('⭐ Favorite');
    favBtn.title = t('Add current color to favorites');
  }

  return { root, update };
}

function hexFromString(s: string): RGBA {
  // 期望 "#RRGGBB"
  const hex = s.startsWith('#') ? s.slice(1) : s;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return {
    r: Number.isNaN(r) ? 0 : r,
    g: Number.isNaN(g) ? 0 : g,
    b: Number.isNaN(b) ? 0 : b,
    a: 1
  };
}

function openImagePicker(
  img: HTMLImageElement,
  url: string,
  onPick: (c: RGBA) => void
): void {
  const overlay = document.createElement('div');
  overlay.className = 'image-picker';
  const stage = document.createElement('div');
  stage.className = 'image-picker__stage';

  // 限制最大展示尺寸
  const maxW = Math.min(window.innerWidth * 0.9, 900);
  const maxH = Math.min(window.innerHeight * 0.8, 700);
  const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
  const w = img.naturalWidth * ratio;
  const h = img.naturalHeight * ratio;

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    toast(t('Canvas not available'));
    URL.revokeObjectURL(url);
    return;
  }
  ctx.drawImage(img, 0, 0);

  const display = document.createElement('canvas');
  display.className = 'image-picker__canvas';
  display.width = w;
  display.height = h;
  const dctx = display.getContext('2d');
  if (!dctx) return;
  dctx.imageSmoothingEnabled = true;
  dctx.drawImage(img, 0, 0, w, h);

  const preview = document.createElement('div');
  preview.className = 'image-picker__preview';
  const tip = document.createElement('div');
  tip.className = 'image-picker__tip';
  tip.textContent = t('Click to pick color · Esc to close');

  const close = () => {
    const cleanup = (overlay as HTMLElement & { _cleanup?: () => void })._cleanup;
    cleanup?.();
    overlay.remove();
    URL.revokeObjectURL(url);
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };
  document.addEventListener('keydown', onKey);

  display.addEventListener('click', (e) => {
    const rect = display.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * img.naturalWidth;
    const y = ((e.clientY - rect.top) / rect.height) * img.naturalHeight;
    const data = ctx.getImageData(Math.floor(x), Math.floor(y), 1, 1).data;
    const rgba: RGBA = { r: data[0], g: data[1], b: data[2], a: data[3] / 255 };
    onPick(rgba);
    close();
  });

  stage.append(display, preview, tip);
  overlay.appendChild(stage);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  // Focus trap
  const focusable = stage.querySelectorAll<HTMLElement>('button, [tabindex="0"]');
  if (focusable.length > 0) focusable[0].focus();

  const trap = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  overlay.addEventListener('keydown', trap);

  document.body.appendChild(overlay);

  // Store cleanup fn
  (overlay as HTMLElement & { _cleanup?: () => void })._cleanup = () => {
    overlay.removeEventListener('keydown', trap);
  };
}
